import { createAction } from "redux-starter-kit";
import axios from "axios";
import jwtDecode from "jwt-decode";
import awsconfig from "../../config/awsconfig";
import { navigate } from "@reach/router";
import { LANGUAGE_STORAGE_KEY } from "../../i18n";

export const loginLoading = createAction("LOGIN_LOADING");
export const loginSuccess = createAction("LOGIN_SUCCESS");
export const loginError = createAction("LOGIN_ERROR");

export const setSocket = createAction("SET_SOCKET");

export const logoutSuccess = createAction("LOGOUT_SUCCESS");
export const setReauthenticate = createAction("SET_REAUTHENTICATE");
export const setRefreshedToken = createAction("SET_REFRESHED_TOKEN");

let refreshTimer = null;

const clearSessionStorage = () => {
  const client_id = awsconfig.REACT_APP_REST_CLIENT_ID;
  [
    "name",
    "token",
    "userName",
    "displayName",
    "tokenExpiry",
    "refresh_token",
    "idToken",
    "user_group",
  ].forEach((key) =>
    localStorage.removeItem(`swobbee.portal.${client_id}.${key}`)
  );
  localStorage.removeItem("client_id");
};

const decodeToken = (body) => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }
  const token = body.access_token;
  const idToken = body.id_token;
  const user_group_name = body.user_group_name;

  const tokenDecoded = jwtDecode(token);
  const idTokenDecoded = jwtDecode(idToken);
  const name = idTokenDecoded["name"];
  const user_group = body.user_group_name;
  const displayName = tokenDecoded["username"];
  const userName = tokenDecoded["username"];
  const tokenExpiry = tokenDecoded["exp"];
  const client_id = tokenDecoded["client_id"];

  const prefix = `swobbee.portal.${awsconfig.REACT_APP_REST_CLIENT_ID}`;
  localStorage.setItem(`${prefix}.name`, name);
  localStorage.setItem(`${prefix}.token`, token);
  localStorage.setItem(`${prefix}.userName`, userName);
  localStorage.setItem(`${prefix}.displayName`, displayName);
  localStorage.setItem(`${prefix}.tokenExpiry`, tokenExpiry);
  localStorage.setItem(`${prefix}.refresh_token`, body.refresh_token);
  localStorage.setItem(`${prefix}.idToken`, idToken);
  localStorage.setItem(`${prefix}.user_group`, user_group_name);
  localStorage.setItem("client_id", client_id);

  return {
    name,
    displayName,
    userName,
    tokenExpiry,
    token,
    tokenDecoded,
    user_group,
    user_group_name,
  };
};

const savePermissionsToLocalStorage = async (permissions) => {
  localStorage.setItem("PERMISSIONS", JSON.stringify(permissions));
};

const saveLanguageToLocalStorage = async (language) => {
  const lng = typeof language === "string" ? language : String(language);
  localStorage.setItem("LANGUAGE", JSON.stringify(lng));
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  } catch (_) {}
};

/**
 * getSession reads auth from localStorage and restores Redux session (single source of truth).
 * Call on app load and on storage events (e.g. login in another tab).
 */
export const getSession = () => async (dispatch) => {
  const client_id = awsconfig.REACT_APP_REST_CLIENT_ID;
  const token = localStorage.getItem(`swobbee.portal.${client_id}.token`);
  const userName = localStorage.getItem(`swobbee.portal.${client_id}.userName`);
  const user_group = localStorage.getItem(
    `swobbee.portal.${client_id}.user_group`
  );
  const displayName = localStorage.getItem(
    `swobbee.portal.${client_id}.displayName`
  );
  const tokenExpiry = localStorage.getItem(
    `swobbee.portal.${client_id}.tokenExpiry`
  );
  const name = localStorage.getItem(`swobbee.portal.${client_id}.name`);

  if (!token) {
    dispatch(setReauthenticate());
    return;
  }

  dispatch(
    loginSuccess({
      token,
      userName,
      displayName,
      tokenExpiry,
      name,
      user_group,
    })
  );
};

// export const refreshToken = () => async (dispatch) => {
//   let url = awsconfig.REACT_APP_PUBLIC_API + "/refresh_token";
//   let refreshToken = localStorage.getItem(
//     `swobbee.portal.${awsconfig.REACT_APP_REST_CLIENT_ID}.refresh_token`
//   );
//   let accessToken = localStorage.getItem(
//     `swobbee.portal.${awsconfig.REACT_APP_REST_CLIENT_ID}.token`
//   );
//   const userName = localStorage.getItem(
//     `swobbee.portal.${awsconfig.REACT_APP_REST_CLIENT_ID}.userName`
//   );
//   const user_group = localStorage.getItem(
//     `swobbee.portal.${awsconfig.REACT_APP_REST_CLIENT_ID}.user_group`
//   );
//   const displayName = localStorage.getItem(
//     `swobbee.portal.${awsconfig.REACT_APP_REST_CLIENT_ID}.displayName`
//   );
//   const name = localStorage.getItem(
//     `swobbee.portal.${awsconfig.REACT_APP_REST_CLIENT_ID}.name`
//   );

//   if (!refreshToken || !accessToken) {
//     clearSessionStorage();
//     dispatch(loginLoading(false));
//     return;
//   }

//   try {
//     const { data } = await axios.post(url, {
//       client_id: awsconfig.REACT_APP_REST_CLIENT_ID,
//       refresh_token: refreshToken,
//       access_token: accessToken,
//     });
//     const { token, tokenExpiry } = decodeToken(data.data, dispatch);
//     dispatch(setRefreshedToken({ token, tokenExpiry }));
//     dispatch(loginLoading(false));
//     dispatch(
//       loginSuccess({
//         token,
//         userName,
//         displayName,
//         tokenExpiry,
//         name,
//         user_group,
//       })
//     );
//     return data;
//   } catch (error) {
//     dispatch(loginLoading(false));
//     clearSessionStorage();
//     window.location = "/";
//     return error;
//   }
// };

export const requestNewPassword = (username) => async (dispatch) => {
  const url = awsconfig.REACT_APP_PUBLIC_API + "/forgot_password";
  try {
    await axios.post(url, {
      username,
      client_id: awsconfig.REACT_APP_REST_CLIENT_ID,
    });
  } catch (error) {
    console.log("q3 error", error);
  }
};

export const loginUser = (username, password) => async (dispatch) => {
  dispatch(loginLoading(true));
  const url = awsconfig.REACT_APP_PUBLIC_API + "/login";
  try {
    const response = await axios.post(url, {
      username,
      password,
      client_id: awsconfig.REACT_APP_REST_CLIENT_ID,
    });

    const data = response.data;
    if (data.status_code === 200) {
      const {
        userName,
        displayName,
        tokenExpiry,
        name,
        user_group,
        token,
      } = decodeToken(data.data);

      if (data.data.permissions) {
        await savePermissionsToLocalStorage(data.data.permissions);
      }
      if (data.data.language) {
        await saveLanguageToLocalStorage(data.data.language);
      }

      dispatch(
        loginSuccess({
          token,
          userName,
          displayName,
          tokenExpiry,
          name,
          user_group,
        })
      );
      return data.data;
    }
  } catch (error) {
    dispatch(loginLoading(false));
    const message =
      error.response?.data?.message || error.message || "Login failed";
    dispatch(loginError(message));
  }
};

export const resetPassword =
  (username, confirmationCode, newPassword) => async (dispatch) => {
    dispatch(loginLoading(true));

    const url = awsconfig.REACT_APP_PUBLIC_API + "/confirm_forgot_password";
    try {
      await axios.post(url, {
        username,
        confirmation_code: confirmationCode,
        new_password: newPassword,
        client_id: awsconfig.REACT_APP_REST_CLIENT_ID,
      });
      navigate("/");
      dispatch(loginLoading(false));
    } catch (error) {
      dispatch(loginLoading(false));
      dispatch(loginError(error.message));
      return error;
    }
  };

export const logoutUser = () => async (dispatch) => {
  try {
    const { data } = await axios.post(
      awsconfig.REACT_APP_PUBLIC_API + "/logout",
      {
        access_token: localStorage.getItem(
          `swobbee.portal.${awsconfig.REACT_APP_REST_CLIENT_ID}.token`
        ),
        client_id: awsconfig.REACT_APP_REST_CLIENT_ID,
      }
    );
    if (data.status_code === 200) {
      localStorage.removeItem("PERMISSIONS");
      localStorage.removeItem("LANGUAGE");
      localStorage.removeItem(LANGUAGE_STORAGE_KEY);
      clearSessionStorage();
      dispatch(logoutSuccess());
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
      sessionStorage.removeItem("SESSIONS");
      // window.location = "/";
    }
  } catch (error) {
    console.log(error);
    localStorage.removeItem("PERMISSIONS");
    localStorage.removeItem("LANGUAGE");
    localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    clearSessionStorage();
    dispatch(logoutSuccess());
    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }
    sessionStorage.removeItem("SESSIONS");
  }
};
